/*
 * TY-058: slide-out cart drawer and the cart count badges.
 *
 * Markup lives in snippets/cart-drawer.liquid. Every cart change asks Shopify
 * for that markup again through the Section Rendering API ("sections" on
 * /cart/add.js and /cart/change.js), and only [data-cart-drawer-content] is
 * swapped. Nothing about a line item is rendered here, so the drawer always
 * matches what Liquid would print.
 *
 * Loaded with defer from layout/theme.liquid. Deferred scripts run once the
 * document is parsed, so the drawer markup already exists, and before
 * DOMContentLoaded, so window.CartDrawer is defined by the time the add-to-cart
 * handler in snippets/ajaxify-cart.liquid needs it.
 *
 * The cart icons stay ordinary links to the cart page. If this file never
 * loads, they keep working, and ajaxify-cart sends the customer to the cart
 * page after an add instead of opening the drawer.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var panel = drawer.querySelector('[data-cart-drawer-panel]');
  var status = drawer.querySelector('[data-cart-drawer-status]');
  var changeUrl = drawer.getAttribute('data-cart-change-url') + '.js';
  var cartUrl = drawer.getAttribute('data-cart-url');
  var rootUrl = drawer.getAttribute('data-root-url');

  var lastFocused = null;
  var queue = Promise.resolve();
  var pending = 0;

  /*================ Open and close ================*/

  function isOpen() {
    return drawer.classList.contains('is-open');
  }

  function open(message) {
    status.textContent = message || '';
    if (isOpen()) return;

    lastFocused = document.activeElement;
    closeMobileMenu();
    lockScroll();
    drawer.classList.add('is-open');
    setExpanded(true);
    panel.focus({ preventScroll: true });
  }

  function close() {
    if (!isOpen()) return;

    drawer.classList.remove('is-open');
    setExpanded(false);
    unlockScroll();

    var target = lastFocused;
    lastFocused = null;
    if (target && target !== document.body && document.contains(target) && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  function setExpanded(expanded) {
    document.querySelectorAll('[data-cart-drawer-open]').forEach(function (link) {
      link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  // The mobile menu sits under the header and would still be open behind the
  // drawer. Its toggle owns the state (snippets/mobile-menu.liquid), so use it.
  function closeMobileMenu() {
    if (!document.body.classList.contains('menu-open')) return;
    var toggle = document.querySelector('.mobile-menu-toggle');
    if (toggle) toggle.click();
  }

  // Hiding the scrollbar would shift the page sideways on desktop; padding
  // the body by the scrollbar's width keeps everything where it was.
  function lockScroll() {
    var scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    document.body.classList.add('cart-drawer-open');
  }

  function unlockScroll() {
    document.body.classList.remove('cart-drawer-open');
    document.body.style.paddingRight = '';
  }

  /*================ Keyboard and focus ================*/

  function focusableElements() {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.getClientRects().length > 0;
    });
  }

  // Capture phase on window, so this runs before any other keydown handler.
  // Keys are only handled while focus is in the drawer. The newsletter popup
  // (sections/newsletter-popup.liquid) can open on its timer above the drawer
  // and takes focus; Esc must then close only the popup, and Tab must move
  // through the popup. Checking before the popup's own handler runs matters:
  // closing the popup hands focus back to the drawer.
  window.addEventListener('keydown', function (e) {
    if (!isOpen()) return;

    var active = document.activeElement;
    if (active && active !== document.body && !panel.contains(active)) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = focusableElements();
    if (!focusable.length) {
      e.preventDefault();
      panel.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, true);

  /*================ Clicks ================*/

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-drawer-open]');
    if (opener) {
      // Let ctrl/cmd/shift-click open the cart page in a new tab or window.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      open();
      return;
    }

    if (!isOpen() || !drawer.contains(e.target)) return;

    if (e.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }

    var action = e.target.closest('[data-cart-action]');
    if (action) {
      changeLine(action);
      return;
    }

    // A pending quantity change has not reached the cart yet, so going to
    // checkout now could leave with the old quantities. Wait for it.
    var checkout = e.target.closest('[data-cart-checkout]');
    if (checkout && pending > 0) {
      e.preventDefault();
      queue.then(function () {
        window.location.href = checkout.href;
      });
    }
  });

  /*================ Cart changes ================*/

  function changeLine(button) {
    var line = button.closest('[data-cart-line]');
    if (!line || line.classList.contains('is-loading')) return;

    var key = line.getAttribute('data-cart-line');
    var quantity = parseInt(button.getAttribute('data-quantity'), 10);
    if (isNaN(quantity) || quantity < 0) return;

    line.classList.add('is-loading');
    setLineError(line, '');
    status.textContent = '';
    setPending(1);

    queue = queue.then(function () {
      return postJSON(changeUrl, {
        id: key,
        quantity: quantity,
        sections: SECTION_ID,
        sections_url: window.location.pathname
      }).then(function (cart) {
        var focus = captureFocus();
        return renderOrRefresh(cart.sections).then(function () {
          restoreFocus(focus);

          // Shopify caps a quantity at the stock on hand instead of failing,
          // so compare what we asked for with what the cart now holds.
          var item = findItem(cart.items, key);
          if (item && item.quantity < quantity) {
            setLineError(findLine(key), 'Sorry, only ' + item.quantity + ' of this item ' + (item.quantity === 1 ? 'is' : 'are') + ' available.');
          }
        });
      }).catch(function (error) {
        line.classList.remove('is-loading');
        setLineError(line, error.customerFacing ? error.message : 'Sorry, your cart could not be updated. Please refresh the page and try again.');
      }).then(function () {
        setPending(-1);
      });
    });
  }

  function setPending(delta) {
    pending += delta;
    drawer.classList.toggle('is-busy', pending > 0);
    panel.setAttribute('aria-busy', pending > 0 ? 'true' : 'false');
  }

  function setLineError(line, message) {
    if (!line) return;
    var error = line.querySelector('[data-cart-line-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function findItem(items, key) {
    if (!items) return null;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) return items[i];
    }
    return null;
  }

  function findLine(key) {
    var lines = drawer.querySelectorAll('[data-cart-line]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-cart-line') === key) return lines[i];
    }
    return null;
  }

  // Re-rendering replaces the button the customer just pressed. Put keyboard
  // focus back on the same control of the same line, or on the panel if the
  // line has gone.
  function captureFocus() {
    var active = document.activeElement;
    if (!active || !panel.contains(active)) return null;
    var line = active.closest('[data-cart-line]');
    return {
      key: line ? line.getAttribute('data-cart-line') : null,
      action: active.getAttribute('data-cart-action')
    };
  }

  function restoreFocus(focus) {
    if (!focus || !isOpen()) return;
    var target = null;
    var line = focus.key ? findLine(focus.key) : null;
    if (line) {
      target = line.querySelector('[data-cart-action="' + focus.action + '"]:not([disabled])') ||
               line.querySelector('[data-cart-action]:not([disabled])');
    }
    (target || panel).focus({ preventScroll: true });
  }

  /*================ Rendering ================*/

  function render(sections) {
    var html = sections && sections[SECTION_ID];
    if (!html) return false;

    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-content]');
    var current = drawer.querySelector('[data-cart-drawer-content]');
    if (!fresh || !current) return false;

    current.replaceWith(document.importNode(fresh, true));
    updateCount(parseInt(fresh.getAttribute('data-cart-item-count'), 10) || 0);
    return true;
  }

  function refresh() {
    return fetch(rootUrl + '?sections=' + SECTION_ID, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart drawer refresh failed (' + response.status + ')');
      return response.json();
    }).then(function (sections) {
      if (!render(sections)) throw new Error('Cart drawer refresh returned no markup');
    });
  }

  function renderOrRefresh(sections) {
    return render(sections) ? Promise.resolve() : refresh();
  }

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-link]').forEach(function (link) {
      link.setAttribute('aria-label', count === 0 ? 'Cart' : 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) {
          throw customerError(data.description || data.message || 'Sorry, your cart could not be updated. Please try again.');
        }
        return data;
      });
    }, function () {
      throw customerError('Sorry, your cart could not be updated. Please check your connection and try again.');
    });
  }

  // Shopify's own error text ("You can only add 2 of this item...") is written
  // for customers, so it is shown as is. Anything else gets a generic message.
  function customerError(message) {
    var error = new Error(message);
    error.customerFacing = true;
    return error;
  }

  /*================ After an add ================*/

  // Called by snippets/ajaxify-cart.liquid with the /cart/add.js response,
  // which carries the re-rendered drawer when "sections" was sent with it.
  function added(item) {
    var title = item && (item.product_title || item.title);
    var message = title ? title + ' has been added to your cart.' : 'Added to your cart.';

    return renderOrRefresh(item && item.sections).then(function () {
      open(message);
    }).catch(function () {
      // The item is in the cart; we just cannot show the drawer. The cart
      // page is the next best confirmation.
      window.location.href = cartUrl;
    });
  }

  // A page restored from the back/forward cache shows the cart as it was when
  // the customer left, which may no longer be true.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      close();
      refresh().catch(function () {});
    }
  });

  window.CartDrawer = {
    sectionId: SECTION_ID,
    open: open,
    close: close,
    isOpen: isOpen,
    refresh: refresh,
    added: added
  };
})();
